import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  message,
  Alert,
  Descriptions,
  Tag,
  Space,
  Modal,
  Input,
  Typography,
  Statistic,
  Row,
  Col,
  Empty,
  Tooltip
} from 'antd';
import {
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  ApiOutlined,
  FileTextOutlined,
  GlobalOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Text } = Typography;

const UserAPIKeyManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // IP白名单相关状态
  const [ipWhitelistModalVisible, setIpWhitelistModalVisible] = useState(false);
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [ipWhitelistLoading, setIpWhitelistLoading] = useState(false);

  // 获取API密钥
  const fetchAPIKey = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/user/api-key');
      
      if (response.data.success) {
        setApiKey(response.data.data);
      } else {
        message.error(response.data.message || '获取API密钥失败');
      }
    } catch (error) {
      console.error('获取API密钥错误:', error);
      message.error(error.response?.data?.message || '获取API密钥失败');
    } finally {
      setLoading(false);
    }
  };

  // 创建API密钥
  const createAPIKey = async () => {
    if (!keyName.trim()) {
      message.warning('请输入密钥名称');
      return;
    }

    setCreating(true);
    try {
      const response = await API.post('/api/user/api-key/create', {
        name: keyName.trim()
      });

      if (response.data.success) {
        message.success('API密钥创建成功');
        setCreateModalVisible(false);
        setKeyName('');
        setShowKey(true); // 自动显示新创建的密钥
        fetchAPIKey();
      } else {
        message.error(response.data.message || '创建失败');
      }
    } catch (error) {
      console.error('创建API密钥错误:', error);
      message.error(error.response?.data?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  // 重置API密钥
  const resetAPIKey = () => {
    Modal.confirm({
      title: '确认重置API密钥',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>重置后，旧密钥将立即失效，所有使用旧密钥的请求都会失败。</p>
          <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
            请确保在重置后立即更新所有使用该密钥的应用！
          </p>
        </div>
      ),
      okText: '确定重置',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await API.post('/api/user/api-key/reset');

          if (response.data.success) {
            message.success('API密钥重置成功');
            setShowKey(true); // 自动显示新密钥
            fetchAPIKey();
          } else {
            message.error(response.data.message || '重置失败');
          }
        } catch (error) {
          console.error('重置API密钥错误:', error);
          message.error(error.response?.data?.message || '重置失败');
        }
      }
    });
  };

  // 删除API密钥
  const deleteAPIKey = () => {
    Modal.confirm({
      title: '确认删除API密钥',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>删除后，该密钥将立即失效，所有使用该密钥的请求都会失败。</p>
          <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
            此操作不可恢复！
          </p>
        </div>
      ),
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await API.delete('/api/user/api-key');

          if (response.data.success) {
            message.success('API密钥已删除');
            setApiKey(null);
            setShowKey(false);
          } else {
            message.error(response.data.message || '删除失败');
          }
        } catch (error) {
          console.error('删除API密钥错误:', error);
          message.error(error.response?.data?.message || '删除失败');
        }
      }
    });
  };

  // 复制密钥
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败，请手动复制');
    });
  };

  // 格式化密钥显示
  const formatKey = (key) => {
    if (!key) return '';
    if (showKey) return key;
    return key.substring(0, 8) + '•'.repeat(key.length - 16) + key.substring(key.length - 8);
  };

  // 获取IP白名单
  const fetchIpWhitelist = async () => {
    try {
      const response = await API.get('/api/user/api-key/ip-whitelist');
      if (response.data.success) {
        setIpWhitelist(response.data.data.ipWhitelist || []);
      }
    } catch (error) {
      console.error('获取IP白名单错误:', error);
      message.error(error.response?.data?.message || '获取IP白名单失败');
    }
  };

  // 打开IP白名单管理弹窗
  const openIpWhitelistModal = () => {
    fetchIpWhitelist();
    setIpWhitelistModalVisible(true);
  };

  // 添加IP到白名单
  const handleAddIp = async () => {
    if (!newIp.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    setIpWhitelistLoading(true);
    try {
      const response = await API.post('/api/user/api-key/ip-whitelist/add', {
        ip: newIp.trim()
      });

      if (response.data.success) {
        message.success('IP已添加到白名单');
        setNewIp('');
        setIpWhitelist(response.data.data.ipWhitelist);
      } else {
        message.error(response.data.message || '添加失败');
      }
    } catch (error) {
      console.error('添加IP错误:', error);
      message.error(error.response?.data?.message || '添加失败');
    } finally {
      setIpWhitelistLoading(false);
    }
  };

  // 从白名单移除IP
  const handleRemoveIp = async (ip) => {
    setIpWhitelistLoading(true);
    try {
      const response = await API.delete('/api/user/api-key/ip-whitelist/remove', {
        data: { ip }
      });

      if (response.data.success) {
        message.success('IP已从白名单移除');
        setIpWhitelist(response.data.data.ipWhitelist);
      } else {
        message.error(response.data.message || '移除失败');
      }
    } catch (error) {
      console.error('移除IP错误:', error);
      message.error(error.response?.data?.message || '移除失败');
    } finally {
      setIpWhitelistLoading(false);
    }
  };

  useEffect(() => {
    fetchAPIKey();
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card
        title={
          <Space>
            <ApiOutlined style={{ fontSize: 20 }} />
            <span>API 密钥管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => navigate('/user/api-documentation')}
            >
              接口文档
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchAPIKey}
              loading={loading}
            >
              刷新
            </Button>
            {!apiKey && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
              >
                创建密钥
              </Button>
            )}
          </Space>
        }
      >
        {/* 使用说明 */}
        <Alert
          message="什么是API密钥？"
          description={
            <div>
              <p>API密钥是您调用系统API接口的身份凭证，用于程序化访问系统功能。</p>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>每个账户只能创建一个激活的API密钥</li>
                <li>请妥善保管您的密钥，不要泄露给他人</li>
                <li>如果密钥泄露，请立即重置或删除</li>
                <li>密钥受QPS、每日调用次数和会话数量限制</li>
                <li>会话限制控制您可以同时创建的AI助手会话数量</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* 密钥信息展示 */}
        {apiKey ? (
          <>
            {/* 密钥统计 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="今日使用"
                    value={apiKey.todayUsage || 0}
                    suffix={`/ ${apiKey.dailyLimit}`}
                    valueStyle={{ color: apiKey.todayUsage >= apiKey.dailyLimit ? '#cf1322' : '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="总调用次数"
                    value={apiKey.totalUsage || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="QPS限制"
                    value={apiKey.qpsLimit}
                    suffix="次/秒"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="每日限制"
                    value={apiKey.dailyLimit}
                    suffix="次/天"
                  />
                </Card>
              </Col>
            </Row>

            {/* 会话统计 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="会话限制"
                    value={apiKey.sessionLimit === 0 ? '不限制' : apiKey.sessionLimit}
                    suffix={apiKey.sessionLimit === 0 ? '' : '个'}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="当前会话数"
                    value={apiKey.sessionCount || 0}
                    suffix={apiKey.sessionLimit > 0 ? `/ ${apiKey.sessionLimit}` : ''}
                    valueStyle={{ 
                      color: apiKey.sessionLimit > 0 && apiKey.sessionCount >= apiKey.sessionLimit 
                        ? '#cf1322' 
                        : '#3f8600' 
                    }}
                  />
                  {apiKey.sessionLimit > 0 && apiKey.sessionCount >= apiKey.sessionLimit && (
                    <Alert
                      message="已达到会话数量上限"
                      description="您已达到允许的最大会话数量，如需创建新会话，请先删除一些旧会话或联系管理员调整限制。"
                      type="warning"
                      showIcon
                      style={{ marginTop: 12 }}
                    />
                  )}
                </Card>
              </Col>
            </Row>

            {/* 密钥详情 */}
            <Card
              title={
                <Space>
                  <KeyOutlined />
                  <span>密钥详情</span>
                  <Tag color={apiKey.status === 'active' ? 'success' : 'default'}>
                    {apiKey.status === 'active' ? '激活' : '已禁用'}
                  </Tag>
                </Space>
              }
              extra={
                <Space>
                  <Button
                    icon={<GlobalOutlined />}
                    onClick={openIpWhitelistModal}
                  >
                    IP白名单
                  </Button>
                  <Button
                    type="primary"
                    ghost
                    icon={<ReloadOutlined />}
                    onClick={resetAPIKey}
                  >
                    重置密钥
                  </Button>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteAPIKey}
                  >
                    删除密钥
                  </Button>
                </Space>
              }
            >
              <Descriptions column={1} bordered>
                <Descriptions.Item label="密钥名称">
                  {apiKey.name}
                </Descriptions.Item>
                <Descriptions.Item label="API Key">
                  <Space style={{ width: '100%' }} direction="vertical">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Input
                        value={formatKey(apiKey.key)}
                        readOnly
                        style={{
                          flex: 1,
                          fontFamily: 'Monaco, Consolas, monospace',
                          fontSize: 13
                        }}
                        suffix={
                          <Space>
                            <Tooltip title={showKey ? '隐藏密钥' : '显示密钥'}>
                              <Button
                                type="text"
                                size="small"
                                icon={showKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                onClick={() => setShowKey(!showKey)}
                              />
                            </Tooltip>
                            <Tooltip title="复制密钥">
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => copyToClipboard(apiKey.key)}
                              />
                            </Tooltip>
                          </Space>
                        }
                      />
                    </div>
                    {showKey && (
                      <Alert
                        message={
                          <span>
                            <SafetyOutlined /> 密钥已显示，请勿分享或泄露
                          </span>
                        }
                        type="warning"
                        showIcon={false}
                        closable
                      />
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="会话限制">
                  {apiKey.sessionLimit === 0 ? (
                    <Tag color="green">不限制</Tag>
                  ) : (
                    <span>
                      {apiKey.sessionLimit} 个
                      {apiKey.sessionCount >= apiKey.sessionLimit && (
                        <Tag color="error" style={{ marginLeft: 8 }}>已达上限</Tag>
                      )}
                    </span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="当前会话数">
                  <span style={{ 
                    color: apiKey.sessionLimit > 0 && apiKey.sessionCount >= apiKey.sessionLimit 
                      ? '#cf1322' 
                      : 'inherit' 
                  }}>
                    {apiKey.sessionCount || 0}
                    {apiKey.sessionLimit > 0 && ` / ${apiKey.sessionLimit}`}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {dayjs(apiKey.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({dayjs(apiKey.createdAt).fromNow()})
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="最后使用">
                  {apiKey.lastUsedAt ? (
                    <>
                      {dayjs(apiKey.lastUsedAt).format('YYYY-MM-DD HH:mm:ss')}
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        ({dayjs(apiKey.lastUsedAt).fromNow()})
                      </Text>
                    </>
                  ) : (
                    <Text type="secondary">从未使用</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="您还没有创建API密钥"
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              立即创建
            </Button>
          </Empty>
        )}
      </Card>

      {/* 创建密钥Modal */}
      <Modal
        title="创建API密钥"
        open={createModalVisible}
        onOk={createAPIKey}
        onCancel={() => {
          setCreateModalVisible(false);
          setKeyName('');
        }}
        confirmLoading={creating}
        okText="创建"
        cancelText="取消"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            message="创建后请立即复制并保存您的API密钥"
            description="为了安全，系统不会再次完整显示您的密钥。"
            type="warning"
            showIcon
          />
          
          <div>
            <Text>密钥名称</Text>
            <Input
              placeholder="例如：生产环境密钥"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              maxLength={50}
              style={{ marginTop: 8 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              为您的密钥设置一个易于识别的名称
            </Text>
          </div>
        </Space>
      </Modal>

      {/* IP白名单管理Modal */}
      <Modal
        title={
          <Space>
            <GlobalOutlined />
            <span>IP 白名单管理</span>
          </Space>
        }
        open={ipWhitelistModalVisible}
        onCancel={() => {
          setIpWhitelistModalVisible(false);
          setNewIp('');
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIpWhitelistModalVisible(false);
            setNewIp('');
          }}>
            关闭
          </Button>
        ]}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 说明 */}
          <Alert
            message="IP白名单说明"
            description={
              <div>
                <p>配置IP白名单后，只有白名单中的IP地址才能使用此API密钥。</p>
                <p style={{ marginBottom: 0 }}>
                  <strong>支持格式：</strong>
                </p>
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>单个IP：192.168.1.1</li>
                  <li>通配符：192.168.1.*</li>
                  <li>CIDR：192.168.1.0/24</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {/* 添加IP */}
          <Card size="small" title="添加IP地址">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="输入IP地址（支持通配符和CIDR）"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                onPressEnter={handleAddIp}
                disabled={ipWhitelistLoading}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddIp}
                loading={ipWhitelistLoading}
              >
                添加
              </Button>
            </Space.Compact>
          </Card>

          {/* IP列表 */}
          <Card 
            size="small" 
            title={
              <span>
                白名单列表 
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {ipWhitelist.length} 个IP
                </Tag>
              </span>
            }
          >
            {ipWhitelist.length === 0 ? (
              <Empty 
                description="暂无IP白名单，所有IP均可访问" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {ipWhitelist.map((ip, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: '#fafafa',
                      borderRadius: 4,
                      border: '1px solid #f0f0f0'
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: 'Monaco, Consolas, monospace',
                        fontSize: 13
                      }}
                    >
                      {ip}
                    </Text>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveIp(ip)}
                      loading={ipWhitelistLoading}
                    >
                      移除
                    </Button>
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Space>
      </Modal>
    </div>
  );
};

export default UserAPIKeyManagement;

